function normalizePath(path: string): string[] {
  return path
    .trim()
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function isArrayIndex(segment: string): boolean {
  return /^\d+$/.test(segment);
}

function cloneBranch<T>(branch: T): T {
  if (Array.isArray(branch)) {
    return [...branch] as T;
  }

  if (branch && typeof branch === 'object') {
    return { ...(branch as Record<string, unknown>) } as T;
  }

  return branch;
}

export function getValueAtPath<TValue = unknown>(source: unknown, path: string): TValue | undefined {
  return normalizePath(path).reduce<unknown>((current, segment) => {
    if (current == null) {
      return undefined;
    }

    if (Array.isArray(current) && isArrayIndex(segment)) {
      return current[Number(segment)];
    }

    if (typeof current === 'object') {
      return (current as Record<string, unknown>)[segment];
    }

    return undefined;
  }, source) as TValue | undefined;
}

export function setValueAtPath<T>(source: T, path: string, value: unknown): T {
  const segments = normalizePath(path);

  if (segments.length === 0) {
    return source;
  }

  const root = cloneBranch(source);
  let cursor: unknown = root;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const nextSegment = segments[index + 1];
    const nextContainerShouldBeArray = isArrayIndex(nextSegment);

    if (Array.isArray(cursor) && isArrayIndex(segment)) {
      const currentValue = cursor[Number(segment)];
      const nextValue =
        currentValue == null
          ? (nextContainerShouldBeArray ? [] : {})
          : cloneBranch(currentValue);

      cursor[Number(segment)] = nextValue;
      cursor = nextValue;
      continue;
    }

    if (cursor && typeof cursor === 'object') {
      const objectCursor = cursor as Record<string, unknown>;
      const currentValue = objectCursor[segment];
      const nextValue =
        currentValue == null
          ? (nextContainerShouldBeArray ? [] : {})
          : cloneBranch(currentValue);

      objectCursor[segment] = nextValue;
      cursor = nextValue;
    }
  }

  const leafSegment = segments[segments.length - 1];

  if (Array.isArray(cursor) && isArrayIndex(leafSegment)) {
    cursor[Number(leafSegment)] = value;
    return root;
  }

  if (cursor && typeof cursor === 'object') {
    (cursor as Record<string, unknown>)[leafSegment] = value;
  }

  return root;
}
